import { TextField } from '@mui/material';
import { addWalletToUserThunk, updateWalletBalanceThunk } from '@redux/features/user-listing.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useEffect, useMemo } from 'react';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

type IProps = {
    show: boolean;
    handleClose: () => void;
    accountId: string;
    onSuccess?: () => void;
};

const AddWalletToUserModal: FC<IProps> = ({ show, handleClose, accountId, onSuccess }) => {
    // ----------------------------------------------------------------
    const dispatch = useAppDispatch();
    const { users } = useAppSelector((state: RootState) => state.userListingState);

    // Find the user by accountId to check if wallet exists
    const user = useMemo(() => {
        if (!users?.content || !accountId) return null;
        return users.content.find((u: any) => u.account?.accountId?.toString() === accountId.toString());
    }, [users, accountId]);

    // Check if user has a wallet
    const hasWallet = user?.account?.wallet != null;
    const currentBalance = user?.account?.wallet?.balance || 0;

    // --------------------------------------------------
    const validationSchema = Yup.object({
        balance: Yup.number().required('Balance Is Required').min(0, 'Balance must be greater than or equal to 0'),
    });

    const handleSubmit = (values: any) => {
        const amountToAdd = parseFloat(values.balance) || 0;

        if (hasWallet) {
            // Increase existing wallet balance by adding to current balance
            const newBalance = currentBalance + amountToAdd;
            dispatch(updateWalletBalanceThunk({ accountId, balance: newBalance })).then((res: any) => {
                if (res.meta.requestStatus === 'fulfilled') {
                    handleClose();
                    formik.resetForm();
                    if (onSuccess) {
                        onSuccess();
                    }
                }
            });
        } else {
            // Add new wallet
            dispatch(addWalletToUserThunk({ accountId, balance: amountToAdd })).then((res: any) => {
                if (res.meta.requestStatus === 'fulfilled') {
                    handleClose();
                    formik.resetForm();
                    if (onSuccess) {
                        onSuccess();
                    }
                }
            });
        }
    };
    // --------------------------------------------------
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            balance: ``,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    // Reset form when modal opens/closes
    useEffect(() => {
        if (show) {
            formik.setFieldValue('balance', '');
        } else {
            formik.resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);
    // --------------------------------------------------

    return (
        <Modal
            show={show}
            onHide={() => handleClose()}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[500px] md:max-h-[80vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">
                    {hasWallet ? 'Increase Wallet Balance' : 'Add Wallet to User'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                    {hasWallet && (
                        <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded">
                            <p className="text-sm text-blue-800 mb-1">
                                <strong>Current Balance:</strong> ${currentBalance.toFixed(2)}
                            </p>
                            {formik.values.balance && !isNaN(parseFloat(formik.values.balance)) && (
                                <p className="text-sm text-green-700 font-semibold">
                                    <strong>New Balance Will Be:</strong> $
                                    {(currentBalance + parseFloat(formik.values.balance)).toFixed(2)}
                                </p>
                            )}
                        </div>
                    )}
                    <TextField
                        type="number"
                        fullWidth
                        size="small"
                        id="balance"
                        name="balance"
                        label={hasWallet ? 'Amount to Increase *' : 'Balance *'}
                        value={formik.values.balance}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.balance && Boolean(formik.errors.balance)}
                        helperText={formik.touched.balance ? String(formik.errors.balance || '') : ''}
                        inputProps={{ min: 0, step: 0.01 }}
                        placeholder={hasWallet ? 'Enter amount to add' : 'Enter initial balance'}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={() => handleClose()}
                    // disabled={isLoading}
                />
                <ButtonBase
                    innerText={hasWallet ? 'Increase Balance' : 'Add Wallet'}
                    className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={formik.handleSubmit}
                    disabled={!formik.values.balance}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default AddWalletToUserModal;
